import React, { useEffect, useRef, useState } from "react";
import styles from './exInventoryBuyItem.module.css'
import ExcessProductTable from "../../components/OtherComps/ProductTable/ExcessProductTable";
import { useDispatch, useSelector } from "react-redux";
import { getExcessStockcountThunk, getExcessStockDataThunk, setExcessAppliedFilters, setExcessFilter, setExcessSearch, setExcessStockPage } from "../../redux/excessSlice";
import CatPagination from "../../utils/Pagination";
import ProductFilter from "../ProductList/ProductFilter";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";

const ExInventoryBuyItem = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch();
    const { excessStock, selectedProducts, excessStockCount, filter, loading, appliedFilters, search } = useSelector((state) => state.excess)
    const products = excessStock?.data || []
    const page = excessStock?.page

    const [quantities, setQuantities] = useState({});
    const [limit, setLimit] = useState(10)

    // Smart search: excess-stock suggestions while typing
    const [suggestions, setSuggestions] = useState([])
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [suggestLoading, setSuggestLoading] = useState(false)
    const searchBoxRef = useRef(null)
    const debounceRef = useRef(null)
    const latestQueryRef = useRef('')

    const product = products?.data
    const totalItems = products?.totalItems
    const itemsPerPage = products?.limit
    const totalPages = products?.totalPages

    // calculate start & end of items -------------------
    const start = (page - 1) * limit + 1;
    const end =
        totalPages === page
            ? totalItems
            : (page - 1) * limit + limit;

    useEffect(() => {
        const data = {
            page,
            limit,
            filters: appliedFilters,
            // Keeps results in line with the search box (e.g. when opened from the main search)
            search
        }
        dispatch(getExcessStockDataThunk(data))
    }, [page, limit, appliedFilters])

    const handleSearch = (value = search) => {
        setShowSuggestions(false)
        const data = {
            page: 1,
            limit,
            filters: appliedFilters,
            search: value
        }
        dispatch(getExcessStockDataThunk(data))
    }

    const handleSearchInput = (value) => {
        dispatch(setExcessSearch(value))
        latestQueryRef.current = value
        if (debounceRef.current) clearTimeout(debounceRef.current)

        const query = value.trim()
        if (!query) {
            setSuggestions([])
            setShowSuggestions(false)
            handleSearch('')
            return
        }

        debounceRef.current = setTimeout(async () => {
            setSuggestLoading(true)
            setShowSuggestions(true)
            try {
                const response = await api.getExcessData({ page: 1, limit: 8, filters: [], search: query })
                // Ignore replies for text the user has already changed
                if (latestQueryRef.current.trim() === query) {
                    // API replies { success, data: [rows], totalItems, ... }
                    const rows = response?.data?.data
                    setSuggestions(Array.isArray(rows) ? rows : rows?.data || [])
                }
            } catch (err) {
                console.log(err)
            } finally {
                setSuggestLoading(false)
            }
        }, 300)
    }

    const handleSuggestionClick = (item) => {
        const value = item?.productNumber || ''
        dispatch(setExcessSearch(value))
        latestQueryRef.current = value
        setSuggestions([])
        handleSearch(value)
    }

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchBoxRef.current && !searchBoxRef.current.contains(event.target)) {
                setShowSuggestions(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [])

    const handleQuantityChange = (productId, value) => {
        setQuantities((prev) => ({
            ...prev,
            [productId]: value,
        }));
    };

    const handleFilterChange = (filters) => {
        dispatch(setExcessFilter(filters))
    };

    useEffect(() => {
        const data = {
            filters: filter
        }
        dispatch(getExcessStockcountThunk(data))
    }, [filter])

    const handleApply = () => {
        dispatch(setExcessAppliedFilters(filter))
        dispatch(setExcessFilter([]))
    }

    const handleReset = () => {
        dispatch(setExcessFilter([]));
        dispatch(setExcessAppliedFilters([]));
        dispatch(setExcessSearch(""));
        const data = {
            page: 1,
            limit,
            filters: [],
            search: ""
        };
        dispatch(getExcessStockDataThunk(data));
    };

    return (
        <>
            <div className={styles.imgBread}>
                <span>Excess Inventory</span>
            </div>

            <div className={styles.exInventoryFlexContainer}>
                <div className={styles.leftColumn}>
                    <div className={styles.optionSecton}>
                        <div onClick={() => navigate('/exInventoryUpload')}>
                            <button className={styles.addBtn} style={{ padding: '12px 50px' }}>Sell/Upload your own inventory</button>
                        </div>
                        <div className={styles.divider}>
                            <div className={styles.hrLine}></div> Or <div className={styles.hrLine}></div>
                        </div>
                    </div>

                    <div className={styles.crossSearchCard}>
                        <p>Search for excess inventory products</p><br />
                        <h3>Items search</h3>
                        <div ref={searchBoxRef} className={styles.smartSearchWrap}>
                            <form className={styles.searchContainer} onSubmit={(e) => { e.preventDefault(); handleSearch(); }}>
                                <input
                                    type="text"
                                    placeholder="Enter part # or manufacturer"
                                    className={styles.searchInput}
                                    value={search || ''}
                                    onChange={(e) => handleSearchInput(e.target.value)}
                                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                                    autoComplete="off"
                                />
                                <button type="submit" className={styles.searchBtn}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M3.33488 11.0342C4.23694 11.68 5.29827 12.0252 6.38462 12.0252C7.10587 12.0252 7.81987 11.873 8.48579 11.5774C9.15169 11.2819 9.75628 10.849 10.2652 10.3037C10.7742 9.75837 11.1775 9.11138 11.4526 8.39981C11.7277 7.68824 11.8692 6.92579 11.8692 6.15596C11.8692 4.99661 11.5484 3.86278 10.9466 2.89782C10.3448 1.93281 9.48872 1.17957 8.48579 0.734475C7.48276 0.289331 6.37861 0.17273 5.31329 0.399772C4.24804 0.626799 3.27055 1.18697 2.50402 2.00826C1.73755 2.82947 1.21627 3.87496 1.00515 5.01218C0.794022 6.14939 0.902328 7.3282 1.31661 8.39981C1.73091 9.47145 2.43291 10.3885 3.33488 11.0342ZM4.04961 2.39971C4.74144 1.90443 5.55402 1.64057 6.38462 1.64057C7.49826 1.64057 8.5678 2.11449 9.35752 2.96062C10.1474 3.80697 10.5923 4.95626 10.5923 6.15596C10.5923 7.05049 10.3447 7.92443 9.88149 8.66721C9.4183 9.40994 8.76066 9.98773 7.99254 10.3286C7.22453 10.6695 6.37988 10.7585 5.56508 10.5849C4.75021 10.4112 4.00069 9.98233 3.41173 9.3513C2.8227 8.7202 2.42087 7.91532 2.25802 7.03812C2.09516 6.16091 2.1788 5.25172 2.4981 4.42578C2.81739 3.59988 3.35768 2.89507 4.04961 2.39971Z" fill="white" stroke="#512DA8" stroke-width="0.2" />
                                        <path d="M14.4588 15.4864C14.5436 15.4869 14.6274 15.4694 14.7054 15.4352C14.7836 15.401 14.8543 15.3507 14.9136 15.2877C14.973 15.2245 15.0198 15.1497 15.0517 15.0677C15.0835 14.9858 15.0999 14.8981 15.0999 14.8097C15.0999 14.7213 15.0835 14.6337 15.0517 14.5517C15.0199 14.4698 14.9731 14.3951 14.9137 14.3319C14.9137 14.3319 14.9136 14.3318 14.9136 14.3318L10.6104 9.71858C10.5513 9.65523 10.4808 9.60457 10.4026 9.56987C10.3245 9.53516 10.2404 9.51719 10.1554 9.51719C10.0703 9.51719 9.98625 9.53516 9.90808 9.56987C9.82994 9.60457 9.7594 9.65523 9.70031 9.71858C9.64123 9.78191 9.5947 9.85673 9.56307 9.9386C9.53144 10.0205 9.51523 10.108 9.51523 10.1962C9.51523 10.2845 9.53144 10.372 9.56307 10.4539C9.5947 10.5357 9.64123 10.6106 9.70031 10.6739L14.0037 15.2874L14.004 15.2877C14.0633 15.3507 14.134 15.401 14.2122 15.4352C14.2902 15.4694 14.3741 15.4869 14.4588 15.4864ZM14.4588 15.4864C14.4586 15.4864 14.4584 15.4864 14.4582 15.4864L14.4588 15.3864L14.4594 15.4864C14.4592 15.4864 14.459 15.4864 14.4588 15.4864Z" fill="white" stroke="#512DA8" stroke-width="0.2" />
                                    </svg>
                                    Search
                                </button>
                            </form>
                            {showSuggestions && (
                                <div className={styles.suggestBox}>
                                    {suggestLoading && suggestions.length === 0 ?
                                        <div className={styles.suggestEmpty}>Searching...</div>
                                        : suggestions.length === 0 ?
                                            <div className={styles.suggestEmpty}>No excess stock found for "{search}"</div>
                                            :
                                            suggestions.map((item) => {
                                                const mfg = item?.productData?.productOverview?.manufacturer?.name
                                                    || (item?.manufacturer && String(item.manufacturer).toLowerCase() !== 'n/a' ? item.manufacturer : '')
                                                return (
                                                    <div
                                                        key={item?._id}
                                                        className={styles.suggestItem}
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onClick={() => handleSuggestionClick(item)}
                                                    >
                                                        <div className={styles.suggestMain}>
                                                            <span className={styles.suggestPart}>{item?.productNumber}</span>
                                                            {mfg && <span className={styles.suggestMfg}>{mfg}</span>}
                                                        </div>
                                                        <span className={styles.suggestStock}>{item?.stock} in stock</span>
                                                    </div>
                                                )
                                            })
                                    }
                                </div>
                            )}
                        </div>
                        <div className={styles.subCatcheckCol}>
                            <div className={styles.displayFlex}>
                                <input type="checkbox" /> <span>Is Active</span>
                            </div>
                            <div className={styles.displayFlex}>
                                <input type="checkbox" /> <span>In stock</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.rightColumn}>
                    <div className={styles.toolListing}>
                        <span> Steps to follow:</span> <br />
                        <ul>
                            <li>
                                Search for excess components by entering a part number or manufacturer in the search bar below.
                            </li>
                            <li>
                                Filter the listings using the "Is Active" and "In Stock" indicators to find exactly what you need.
                            </li>
                            <li>
                                Select the items you want, specify quantities, and submit your excess stock inquiry to receive quotes directly.
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {loading ?
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 10 }}>
                    <img src="/assets/__Iphone-spinner-1.gif" alt="" style={{ width: 35, height: 35 }} />
                </div>
                :
                <>
                    {excessStock?.data?.filters?.length > 0 &&
                        <ProductFilter
                            filterData={excessStock?.data?.filters}
                            onFilterChange={handleFilterChange}
                            filter={filter}
                            count={excessStockCount?.data?.filteredCount}
                            loading={excessStockCount?.loading}
                            handleSubmit={handleApply}
                            handleReset={handleReset}
                            search={search}
                        />
                    }
                    <div className={styles.sortedTable}>
                        {product?.length > 0 ?
                            <>
                                <div className={styles.sortedFilter}>
                                    <div style={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
                                        {filter?.length > 0 &&
                                            <div>
                                                {excessStockCount?.loading ?
                                                    <img src="/assets/__Iphone-spinner-1.gif" alt="" style={{ width: 25, height: 25 }} />
                                                    :
                                                    <p>{excessStockCount?.data?.filteredCount} of</p>
                                                }
                                            </div>
                                        }
                                        <div>
                                            {totalItems} Results
                                        </div>
                                    </div>
                                </div>
                                <ExcessProductTable
                                    products={product}
                                    quantities={quantities}
                                    handleQuantityChange={handleQuantityChange}
                                    hide={"hide"}
                                    selectedProducts={selectedProducts}
                                />
                                <CatPagination
                                    start={start}
                                    end={end}
                                    totalItems={totalItems}
                                    itemsPerPage={itemsPerPage}
                                    setPage={setExcessStockPage}
                                    page={page}
                                />
                            </>
                            :
                            <div style={{ display: 'flex', justifyContent: 'center', padding: 10, fontSize: 14 }}>No Enquiries Found</div>
                        }
                        {selectedProducts?.length > 0 &&
                            <div className={styles.listingBtns}>
                                <button className={styles.addBtn} onClick={() => navigate(`/exInventEnquiryForm`, { state: selectedProducts })}>Submit</button>
                            </div>
                        }
                    </div>
                </>
            }
        </>
    )
}

export default ExInventoryBuyItem